import { experimentsReady, getVariationWithConfig, loadExperiments } from '@jumpcloud/ui-experiments';
import ConfigService from 'client/util/ConfigService';

import Vue from 'vue';

const getEnvKey = () => ConfigService.signupSplitApiKey || '';

const retryCallLimit = 10;
const retryInterval = 100; // milliseconds based on a fast API response

/**
 * A list of experiments. Used to compare against ui-experiments getVariation()
 * @property {String} flagName - The flagName of the experiment
 * @property {String} startDate - The date the experiment was started
 * @property {Object} variations - An object of the names of the possible variations
 * a user may be shown
 */
const experiments = {
  // used in unit testing
  testExperiment: {
    flagName: 'Unit_Test_Experiment',
    startDate: '2023-10-04',
    variations: {
      default: 'off',
      variation: 'on',
    },
  },

  signupDataCenterSelection: {
    flagName: 'Signup_DataCenter_Selection',
    startDate: '2024-08-07',
    variations: {
      off: 'off',
      on: 'on',
    },
  },
};

// --------------------------------------------------------------------------
// Actions
// --------------------------------------------------------------------------
/**
 * Private action to get all active experiments and their current variants for org
 */
function fetchExperiments({ commit }) {
  const variations = {};

  Object.values(experiments).forEach((experiment) => {
    variations[experiment.flagName] = {
      variation: getVariationWithConfig(experiment.flagName).treatment,
      config: JSON.parse(getVariationWithConfig(experiment.flagName).config),
    };
  });

  commit('SET_EXPERIMENTS', variations);
}

async function initializeExperiments({
  commit,
  dispatch,
  rootState,
  state,
}) {
  try {
    const envKey = getEnvKey();
    const { visitorId } = rootState;

    loadExperiments(visitorId, envKey, { userConsent: 'GRANTED' });
  } catch (e) {
    // eslint-disable-next-line  no-console
    console.error(`Error initializing experiments library: ${e.toString()}`);
    // don't do anything, just catch the error so we can move on without breaking
  }

  let waitForUpdate = false;

  const urlParams = new URLSearchParams(window.location.search);
  const localhostParams = urlParams.get('jcUiExperiments');

  if (localhostParams && localhostParams === 'disabled') {
    commit('SET_ARE_EXPERIMENTS_LOADED', true);
  } else {
    let retry = 0;

    const fetchingExperiments = setInterval(async () => {
      if (experimentsReady()) {
        waitForUpdate = true;

        await dispatch('fetchExperiments');
        commit('SET_ARE_EXPERIMENTS_LOADED', true);

        clearInterval(fetchingExperiments);
      } else if (retry <= retryCallLimit) {
        retry += 1;
      } else {
        commit('SET_ARE_EXPERIMENTS_LOADED', true);
        // Move on from failed load but log an error
        clearInterval(fetchingExperiments);
        // eslint-disable-next-line no-console
        console.error('Failed to load experiments.');
      }
    }, retryInterval);
  }

  // wait for state to be updated or if fetching experiments fails then move on
  await new Promise((resolve) => {
    const experimentsStateUpdating = setInterval(async () => {
      if (!waitForUpdate || state.areExperimentsLoaded) {
        clearInterval(experimentsStateUpdating);
        resolve();
      }
    }, retryInterval);
  });
}

// --------------------------------------------------------------------------
// Getters
// --------------------------------------------------------------------------
/**
 * Gets the treatment/variation name of the given experiment
 * @param {String} experimentKey - key of experiment in the experiment const above
 * @returns {String} - Returns the key
 */
function getExperimentVariation(state) {
  return (experimentKey) => state.experiments[experiments[experimentKey]?.flagName]?.variation ?? '';
}

/**
 * Gets the treatment/variation config of the given experiment
 * @param {String} experimentKey - key of experiment in the experiment const above
 * @returns {Object} - Returns the config object from Split
 */
function getExperimentVariationConfig(state) {
  return (experimentKey) => state.experiments[experiments[experimentKey]?.flagName]?.config ?? '';
}

/**
 * Determines if the current experiment treatment/variation matches the given treatment name
 * @param {String} experimentKey - key of experiment in the experiment const above
 * @param {String} variationsKey - key of variant in the variations object in the given experiment
 * @returns {Boolean} - Returns true if current experiment treatment matches given variation name
 */
function getIsExperimentVariation(state) {
  return (experimentKey, variationsKey) => {
    const currentVariation = state.experiments[experiments[experimentKey]?.flagName]?.variation ?? '';
    const variation = experiments[experimentKey].variations[variationsKey] ?? '';

    return !!(currentVariation && currentVariation === variation);
  };
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

function SET_ARE_EXPERIMENTS_LOADED(state, isLoaded) {
  Vue.set(state, 'areExperimentsLoaded', isLoaded);
}

function SET_EXPERIMENTS(state, expVariations) {
  Vue.set(state, 'experiments', expVariations);
}

export const createExperimentsModule = () => ({
  namespaced: true,
  state: {
    experiments: {},
    areExperimentsLoaded: false,
  },

  actions: {
    fetchExperiments,
    initializeExperiments,
  },

  getters: {
    getExperimentVariation,
    getExperimentVariationConfig,
    getIsExperimentVariation,
  },

  mutations: {
    SET_EXPERIMENTS,
    SET_ARE_EXPERIMENTS_LOADED,
  },
});

export default createExperimentsModule();
